import { ComponentPropsWithoutRef, useEffect, useState } from 'react'

import { useTimeAgo } from '@sparkai/hooks'
import {
  cn,
  formatTimezoneDate,
  formatTimezoneDateTime,
  formatTimezoneTime,
  type AppTimezonePreference,
} from '@sparkai/utils'

export type TimeAgoProps = ComponentPropsWithoutRef<'p'> & {
  timestamp: number
  isShort?: boolean
  timezonePreference?: AppTimezonePreference
}

function pluralize(value: number, unit: string): string {
  const label = value === 1 ? unit : `${unit}s`
  return `${value} ${label} ago`
}

function getPreviousDateKey(dateKey: string) {
  const [year, month, day] = dateKey.split('-').map(Number)
  const previousDate = new Date(Date.UTC(year, month - 1, day - 1))
  return [
    previousDate.getUTCFullYear(),
    String(previousDate.getUTCMonth() + 1).padStart(2, '0'),
    String(previousDate.getUTCDate()).padStart(2, '0'),
  ].join('-')
}

function computeTimezoneTimeAgo(
  timestamp: number,
  isShort: boolean | undefined,
  timezonePreference: AppTimezonePreference
): string {
  const timeDiff = Date.now() - timestamp

  const seconds = Math.floor(timeDiff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const weeks = Math.floor(days / 7)
  const months = Math.floor(days / 30)
  const years = Math.floor(days / 365)

  const notificationDateKey = formatTimezoneDate(timestamp, timezonePreference, { datePattern: 'yyyy-MM-dd' })
  const todayDateKey = formatTimezoneDate(Date.now(), timezonePreference, { datePattern: 'yyyy-MM-dd' })
  const yesterdayDateKey = getPreviousDateKey(todayDateKey)

  if (notificationDateKey === yesterdayDateKey) {
    return isShort
      ? '1d'
      : `yesterday, ${formatTimezoneTime(timestamp, timezonePreference, { includeTimezoneIndicator: true })}`
  }

  if (seconds < 60) return isShort ? 'now' : 'just now'
  if (minutes < 60) return `${minutes} min ago`
  if (hours < 24) return isShort ? `${hours}h` : pluralize(hours, 'hour')

  if (days < 7) {
    return isShort
      ? `${days}d`
      : formatTimezoneDateTime(timestamp, timezonePreference, {
          datePattern: 'MMMM do, yyyy',
          includeTimezoneIndicator: true,
        })
  }

  if (weeks < 4) return pluralize(weeks, 'week')
  if (months < 12) return pluralize(months, 'month')

  return pluralize(years, 'year')
}

export function TimeAgo({ timestamp, isShort, timezonePreference, ...props }: TimeAgoProps) {
  const legacyLabel = useTimeAgo(timestamp, isShort)
  const [timezoneLabel, setTimezoneLabel] = useState<string>(() =>
    timezonePreference ? computeTimezoneTimeAgo(timestamp, isShort, timezonePreference) : ''
  )

  useEffect(() => {
    if (!timezonePreference) {
      return () => undefined
    }

    const updateTimeAgo = () =>
      setTimezoneLabel(computeTimezoneTimeAgo(timestamp, isShort, timezonePreference))

    updateTimeAgo()
    const intervalId = setInterval(updateTimeAgo, 60000)

    return () => clearInterval(intervalId)
  }, [isShort, timestamp, timezonePreference])

  return (
    <p {...props} className={cn('chromatic-ignore', props.className)}>
      {timezonePreference ? timezoneLabel : legacyLabel}
    </p>
  )
}
